import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { FactorStatusType, FactorType, FactorVerificationStatus, FactorVerificationStatusType } from '@app/shared/models/mfa';
import { SmashVerificationCodeComponent } from '@app/shared/modules/smash-verification-code/smash-verification-code/smash-verification-code.component';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, first, map, withLatestFrom } from 'rxjs';
import { MfaActions } from '../../../store/actions';
import * as fromSignin from '../../../store/reducers';

type ApplicationConfigurationStep = 'qrCode' | 'otpVerification';

@Component({
  selector: 'app-mfa',
  templateUrl: './mfa.component.html',
  styleUrls: ['./mfa.component.scss']
})
export class MfaComponent implements OnInit {

  mfaRequired$: Observable<Boolean>;
  mfaDefaultFactor$: Observable<any>;
  mfaFactors$: Observable<{ [key in FactorType]: { status: FactorStatusType, verificationStatus: FactorVerificationStatusType, secret?: string } }>;

  mfaSendOtpLoading$: Observable<Boolean>;
  mfaSendOtpLoaded$: Observable<Boolean>;
  mfaSendOtpError$: Observable<any>;

  verifyEmailFactorLoading$: Observable<boolean>;
  verifyEmailFactorLoaded$: Observable<boolean>;
  verifyEmailFactorError$: Observable<any>;

  verifyApplicationFactorLoading$: Observable<boolean>;
  verifyApplicationFactorLoaded$: Observable<boolean>;
  verifyApplicationFactorError$: Observable<any>;

  verifyOtpLoading$: Observable<Boolean>;
  verifyOtpLoaded$: Observable<Boolean>;
  verifyOtpError$: Observable<any>;

  mfaApplicationSecret$: Observable<string>;

  createMfaApplicationFactorSecretLoading$: Observable<Boolean>;
  createMfaApplicationFactorSecretLoaded$: Observable<Boolean>;
  createMfaApplicationFactorSecretError$: Observable<any>;

  mfaError$: Observable<any>;
  mfaToken$: Observable<string>;

  @ViewChild('verificationCode') verificationCodeComponent: SmashVerificationCodeComponent;

  selectedFactor: FactorType;
  otherFactors: FactorType[];
  decodedToken: any;
  applicationConfigurationStep: ApplicationConfigurationStep = 'qrCode';

  factorTranslations: { [key in FactorType]: string } = {
    email: 'MFA_CHALLENGE_USE_EMAIL',
    application: 'MFA_CHALLENGE_USE_APPLICATION',
  };

  constructor(
    private store$: Store<fromRoot.State & fromSignin.State>,
    private actions$: Actions,
    private tokenService: TokenService,
    private router: Router,
  ) {
    this.mfaToken$ = this.store$.select(fromSignin.getMfaToken);
    this.mfaRequired$ = this.store$.select(fromSignin.getMfaRequired);
    this.mfaDefaultFactor$ = this.store$.select(fromSignin.getMfaDefaultFactor);
    this.mfaFactors$ = this.store$.select(fromSignin.getMfaFactors);
    this.mfaSendOtpLoading$ = this.store$.select(fromSignin.getMfaSendOtpLoading);
    this.mfaSendOtpLoaded$ = this.store$.select(fromSignin.getMfaSendOtpLoaded);
    this.mfaSendOtpError$ = this.store$.select(fromSignin.getMfaSendOtpError);

    this.verifyApplicationFactorLoading$ = this.store$.select(fromSignin.getVerifyApplicationFactorLoading);
    this.verifyApplicationFactorLoaded$ = this.store$.select(fromSignin.getVerifyApplicationFactorLoaded);
    this.verifyApplicationFactorError$ = this.store$.select(fromSignin.getVerifyApplicationFactorError);

    this.verifyEmailFactorLoading$ = this.store$.select(fromSignin.getVerifyEmailFactorLoading);
    this.verifyEmailFactorLoaded$ = this.store$.select(fromSignin.getVerifyEmailFactorLoaded);
    this.verifyEmailFactorError$ = this.store$.select(fromSignin.getVerifyEmailFactorError);

    this.verifyOtpLoading$ = this.store$.select(fromSignin.getMfaverifyOtpLoading);
    this.verifyOtpLoaded$ = this.store$.select(fromSignin.getMfaverifyOtpLoaded);
    this.verifyOtpError$ = this.store$.select(fromSignin.getMfaverifyOtpError);

    this.createMfaApplicationFactorSecretLoading$ = this.store$.select(fromSignin.getCreateMfaApplicationFactorSecretLoading);
    this.createMfaApplicationFactorSecretLoaded$ = this.store$.select(fromSignin.getCreateMfaApplicationFactorSecretLoaded);
    this.createMfaApplicationFactorSecretError$ = this.store$.select(fromSignin.getCreateMfaApplicationFactorSecretError);

    this.mfaApplicationSecret$ = this.store$.select(fromSignin.getMfaApplicationSecret);

    this.mfaError$ = this.store$.select(fromSignin.getMfaError);
  }

  ngOnInit(): void {
    this.setDefaultFactor();
    this.setDecodedToken();
  }

  setDefaultFactor() {
    this.mfaFactors$.pipe(first(factors => !!factors), withLatestFrom(this.mfaDefaultFactor$)).subscribe(([factors, defaultFactor]) => {
      this.selectedFactor = Object.keys(factors).find(f => f === defaultFactor) as FactorType;
      if (this.selectedFactor === 'email') {
        this.sendEmailOtp();
      } else if (this.selectedFactor === 'application' && factors.application.verificationStatus === 'NotVerified') {
        this.createMfaApplicationFactorSecret();
      }
      this.otherFactors = Object.keys(factors).filter(f => f !== this.selectedFactor) as FactorType[];
    });
  }

  setDecodedToken() {
    this.mfaToken$.pipe(first()).subscribe(token => {
      this.decodedToken = this.tokenService.decodeToken(token);
    });
  }

  selectFactor(factor: FactorType) {
    this.mfaFactors$.pipe(first()).subscribe(factors => {
      this.selectedFactor = factor;
      if (factor === 'email') {
        this.sendEmailOtp();
      } else if (this.selectedFactor === 'application' && factors.application.verificationStatus === 'NotVerified') {
        this.createMfaApplicationFactorSecret();
      }
      this.otherFactors = Object.keys(factors).filter(f => f !== factor) as FactorType[];
    });
  }

  isSelectedFactorVerified(): Observable<boolean> {
    return this.mfaFactors$.pipe(map(factors => factors[this.selectedFactor].verificationStatus === FactorVerificationStatus.verified));
  }

  verifyOtp(factor: FactorType, otp: string) {
    this.store$.dispatch(MfaActions.VerifyOtp({ factor, otp }));
    this.actions$.pipe(ofType(MfaActions.VerifyOtpSuccess)).subscribe(() => {
      this.verificationCodeComponent.reset();
    });
    this.actions$.pipe(ofType(MfaActions.VerifyOtpFailure)).subscribe(() => {
      this.verificationCodeComponent.reset();
    });
  }

  sendEmailOtp() {
    this.store$.dispatch(MfaActions.SendOtp({ factor: 'email' }));
  }

  createMfaApplicationFactorSecret() {
    this.store$.dispatch(MfaActions.CreateMfaFactorSecret({ factor: 'application' }));
  }

  verifyApplicationFactor(otp: string): void {
    this.store$.dispatch(MfaActions.VerifyApplicationFactor({ otp: otp }));
    this.actions$.pipe(ofType(MfaActions.VerifyApplicationFactorSuccess), first()).subscribe(() => {
      this.verificationCodeComponent.reset();
    });
    this.actions$.pipe(ofType(MfaActions.VerifyApplicationFactorFailure), first()).subscribe(() => {
      this.verificationCodeComponent.reset();
    });
  }

  setApplicationConfigurationStep(step: ApplicationConfigurationStep) {
    this.applicationConfigurationStep = step;
  }

  back() {
    this.store$.dispatch(MfaActions.ResetMfa());
    this.router.navigate(['/signin']);
  }
}
